import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  message,
  Row,
  Col,
  Tabs,
  Select,
  InputNumber,
  Spin,
  Tooltip,
} from "antd";
import { UploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { IoMdArrowRoundBack } from "react-icons/io";

const { Option } = Select;

export default function EmployeeOperation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [profilePic, setProfilePic] = useState(null);
  const [preview, setPreview] = useState(null);
  const [initialData, setInitialData] = useState({});

  useEffect(() => {
    if (id) fetchEmployeeData();
  }, [id]);

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/employee_operation.php?id=${id}`);
      if (res.data.success) {
        const data = res.data.data;
        setInitialData(data);
        form.setFieldsValue(data);
        setPreview(
          data.profile_pic
            ? `https://smts.org.in/api/${data.profile_pic.replace(/\\/g, "/")}`
            : null
        );
      } else message.error(res.data.message);
    } catch {
      message.error("Failed to load employee details");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    const changed = {};
    Object.keys(values).forEach((key) => {
      if (values[key] !== initialData[key]) changed[key] = values[key];
    });

    if (Object.keys(changed).length === 0 && !profilePic) {
      message.info("No changes detected");
      return;
    }

    const formData = new FormData();
    formData.append("id", id);
    Object.entries(changed).forEach(([k, v]) => formData.append(k, v));
    if (profilePic) formData.append("profile_pic", profilePic);

    setLoading(true);
    try {
      const res = await axiosInstance.post("/employee_operation.php", formData);
      if (res.data.success) {
        message.success("Employee updated successfully!");
        fetchEmployeeData();
      } else message.error(res.data.message);
    } catch {
      message.error("Error updating employee");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this employee?"))
      return;

    try {
      setLoading(true);
      const res = await axiosInstance.delete("/employee_operation.php", {
        data: { id: id }, // ✅ Correct way to send delete payload
      });

      if (res.data.success) {
        message.success("Employee deleted successfully!");
        navigate("/employees");
      } else {
        message.error(res.data.message || "Failed to delete employee");
      }
    } catch (err) {
      console.error(err);
      message.error("Delete failed due to a network or server issue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
      >
        <div className="header flex items-center gap-2 border-b pb-1 border-gray-200 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <IoMdArrowRoundBack size={20} className="text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-800">
            Update Employee
          </h1>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Spin size="large" />
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Tabs
              defaultActiveKey="1"
              items={[
                {
                  key: "1",
                  label: "Personal Info",
                  children: (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Full Name" name="name">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Email" name="email">
                          <Input type="email" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Phone" name="phone">
                          <Input maxLength={10} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Address" name="address">
                          <Input.TextArea rows={2} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Aadhar" name="aadhar">
                          <Input maxLength={16} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Salary" name="salary">
                          <Input maxLength={16} />
                        </Form.Item>
                      </Col>
                    </Row>
                  ),
                },
                {
                  key: "2",
                  label: "Bank & Role",
                  children: (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Bank" name="bank_details">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Account No" name="account_nb">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="IFSC Code" name="ifsc_code">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Branch" name="branch">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Role" name="role">
                          <Select>
                            <Option value="admin">Admin</Option>
                            <Option value="manager">Manager</Option>
                            <Option value="employee">Employee</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Status" name="status">
                          <Select>
                            <Option value="1">Active</Option>
                            <Option value="0">Inactive</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Points" name="points">
                          <InputNumber
                            min={0}
                            style={{ width: "100%" }}
                            disabled
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  ),
                },
                {
                  key: "3",
                  label: "Credentials & Profile",
                  children: (
                    <>
                      <Form.Item label="Password" name="password">
                        <Input.Password placeholder="Leave blank to keep unchanged" />
                      </Form.Item>
                      <Form.Item label="Profile Picture">
                        <Upload
                          beforeUpload={(file) => {
                            setProfilePic(file);
                            setPreview(URL.createObjectURL(file));
                            return false;
                          }}
                          showUploadList={false}
                        >
                          <Button icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                        {preview && (
                          <div className="mt-3">
                            <img
                              src={preview}
                              alt="preview"
                              className="w-[100px] h-[100px] rounded-full object-cover border"
                            />
                          </div>
                        )}
                      </Form.Item>
                    </>
                  ),
                },
              ]}
            />

            <div className="flex gap-2 mt-6">
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Changes
              </Button>
              <Button danger loading={loading} onClick={handleDelete}>
                Delete Employee
              </Button>
            </div>
          </Form>
        )}
      </Card>
    </div>
  );
}
